import type { CSSProperties } from "react";
import { DEFAULT_CONFIDENCE_THRESHOLD } from "@/utils/predictions";
import cssColorNames from "css-color-names";

export const DEFAULT_LABEL = "object";

// CSS color names mapped to hex values (from css-color-names package)
// All color names are normalized to lowercase for lookup
const COLOR_NAMES: Record<string, string> = Object.fromEntries(
    Object.entries(cssColorNames).map(([name, hex]) => [name.toLowerCase(), hex.toLowerCase()])
);

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {Array}           The RGB representation
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param   {number}  r       The red color value
 * @param   {number}  g       The green color value
 * @param   {number}  b       The blue color value
 * @return  {Array}           The HSL representation
 */
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0; // achromatic by default
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return [h, s, l];
}

/**
 * Converts an RGB component to its hexadecimal representation.
 * @param {number} c - The RGB component (0-255).
 * @return {string} The hex string (00-FF).
 */
function componentToHex(c: number): string {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
}

/**
 * Converts RGB color values to a hex string.
 * @param {number} r - Red component.
 * @param {number} g - Green component.
 * @param {number} b - Blue component.
 * @return {string} The hex color string (e.g., "#RRGGBB").
 */
function rgbToHex(r: number, g: number, b: number): string {
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

function generateColorFromHash(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
        const char = text.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }

    const h = ((hash & 0xffffff) % 360) / 360;
    const s = 0.7 + ((hash & 0xff) % 30) / 100;
    const l = 0.35 + (((hash & 0xff00) >> 8) % 15) / 100;

    const [r, g, b] = hslToRgb(h, s, l);
    return rgbToHex(r, g, b);
}

function getColorFromColorNames(text: string): string | null {
    const lowerText = text.toLowerCase();
    const words = lowerText.split(/\s+/);

    // First, check consecutive word combinations (up to 3 words)
    // Check longest combinations first to prioritize multi-word colors
    for (let i = 0; i < words.length; i++) {
        for (let length = 3; length >= 1; length--) {
            const j = i + length;
            if (j > words.length) continue;
            const combined = words.slice(i, j).join("");
            const hexColor = COLOR_NAMES[combined];
            if (hexColor !== undefined) {
                return hexColor;
            }
        }
    }

    // Then check each individual word
    for (const word of words) {
        const hexColor = COLOR_NAMES[word];
        if (hexColor !== undefined) {
            return hexColor;
        }
    }
    return null;
}

export function generateColorFromText(text: string): string {
    if (text === DEFAULT_LABEL) {
        return "#6d28d9";
    }

    const colorFromNames = getColorFromColorNames(text);
    if (colorFromNames) {
        return colorFromNames;
    }

    return generateColorFromHash(text);
}

export function getLabelColor(
    promptLabels: Map<string, { customColor?: string }> | undefined,
    name: string
): string {
    const promptLabel = promptLabels?.get(name);
    return promptLabel?.customColor || generateColorFromText(name);
}

export function updateLabelColor(
    updatePromptLabels: (
        labels: Array<{ name: string; confidenceThreshold: number; customColor: string }>
    ) => void,
    promptLabels: Map<string, { confidenceThreshold?: number; customColor?: string }> | undefined,
    name: string,
    newColor: string
) {
    const promptLabel = promptLabels?.get(name);
    updatePromptLabels([
        {
            name,
            confidenceThreshold: promptLabel?.confidenceThreshold || DEFAULT_CONFIDENCE_THRESHOLD,
            customColor: newColor
        }
    ]);
}

/**
 * Darkens a hex color by reducing its lightness.
 * @param {string} hexColor - The hex color string (e.g., "#RRGGBB").
 * @param {number} amount - The amount to darken (0-1, where 0.3 = 30% darker).
 * @return {string} The darkened hex color string.
 */
export function darkenColor(hexColor: string, amount: number = 0.3): string {
    // Parse hex color
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    // Convert to HSL
    const [h, s, l] = rgbToHsl(r, g, b);

    // Reduce lightness
    const newL = Math.max(0, l - amount);

    // Convert back to RGB and hex
    const [newR, newG, newB] = hslToRgb(h, s, newL);
    return rgbToHex(newR, newG, newB);
}

/**
 * Calculates the relative luminance of a color using WCAG formula.
 * Returns a value between 0 (darkest) and 1 (lightest).
 * @param {number} r - Red component (0-255).
 * @param {number} g - Green component (0-255).
 * @param {number} b - Blue component (0-255).
 * @return {number} Relative luminance (0-1).
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map((val) => {
        const normalized = val / 255;
        return normalized <= 0.03928
            ? normalized / 12.92
            : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Parses a hex color string to RGB values.
 * @param {string} hex - Hex color string (e.g., "#RRGGBB" or "#RGB").
 * @return {[number, number, number] | null} RGB values [r, g, b] or null if invalid.
 */
function hexToRgb(hex: string): [number, number, number] | null {
    const cleanHex = hex.replace("#", "");
    if (cleanHex.length === 3) {
        const r = parseInt(cleanHex[0] + cleanHex[0], 16);
        const g = parseInt(cleanHex[1] + cleanHex[1], 16);
        const b = parseInt(cleanHex[2] + cleanHex[2], 16);
        return [r, g, b];
    } else if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.slice(0, 2), 16);
        const g = parseInt(cleanHex.slice(2, 4), 16);
        const b = parseInt(cleanHex.slice(4, 6), 16);
        return [r, g, b];
    }
    return null;
}

/**
 * Calculates the contrast ratio between two colors using WCAG formula.
 * @param {number} r1, g1, b1 - RGB values for the first color (0-255).
 * @param {number} r2, g2, b2 - RGB values for the second color (0-255).
 * @return {number} Contrast ratio (1-21, where 1 is no contrast and 21 is maximum).
 */
function getContrastRatio(
    r1: number,
    g1: number,
    b1: number,
    r2: number,
    g2: number,
    b2: number
): number {
    const lum1 = getRelativeLuminance(r1, g1, b1);
    const lum2 = getRelativeLuminance(r2, g2, b2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Checks if white text has sufficient contrast against a color background
 * Returns false if the color is undefined or invalid.
 * @param {string} color - The background color in hex format (e.g., "#RRGGBB").
 * @return {boolean} True if white text meets contrast requirements on this background, false otherwise.
 */
function isDarkColor(color: string | undefined): boolean {
    if (!color) {
        return false;
    }

    const rgb = hexToRgb(color);
    if (!rgb) {
        return false;
    }

    const [r, g, b] = rgb;
    const contrastRatio = getContrastRatio(255, 255, 255, r, g, b);
    const CONTRAST_THRESHOLD = 2.5;

    return contrastRatio >= CONTRAST_THRESHOLD;
}

/**
 * Determines the optimal text color (black or white) for a given background color
 * based on WCAG luminance calculations.
 * @param {string} backgroundColor - The background color in hex format (e.g., "#RRGGBB").
 * @return {string} The optimal text color as a hex string ("#000000" or "#ffffff").
 */
export function getContrastTextColor(backgroundColor: string): string {
    return isDarkColor(backgroundColor) ? "#ffffff" : "#000000";
}

/**
 * Gets the chip style object based on label color contrast.
 * @param {string} labelColor - The label color in hex format (e.g., "#RRGGBB").
 * @return {CSSProperties} The style object for the chip component.
 */
export function getChipStyle(labelColor: string | undefined): CSSProperties {
    if (!labelColor) {
        return { backgroundColor: "rgba(0, 0, 0, 0.1)", color: "rgba(0, 0, 0, 0.7)" };
    }

    if (isDarkColor(labelColor)) {
        return {
            backgroundColor: `${labelColor}1A`, // 10% opacity
            color: labelColor
        };
    }
    // For light colors, use a darkened version of the label color for better contrast
    const darkColor = darkenColor(labelColor, 0.6);
    const darkerColor = darkenColor(labelColor, 0.2);

    return {
        backgroundColor: `${darkerColor}33`, // 20% opacity (0.2 * 255 ≈ 51 = 0x33)
        color: darkColor
    };
}

/**
 * Adds or modifies the alpha component of a color string.
 * Supports rgb, rgba, hex, and named CSS colors.
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context to parse named colors.
 * @param {string} color - The input color string.
 * @param {number} alpha - The desired alpha value (0-1).
 * @return {string} The resulting rgba string.
 */
export function colorWithAlpha(
    ctx: CanvasRenderingContext2D,
    color: string,
    alpha: number
): string {
    // Ensure alpha is clamped between 0 and 1
    const clampedAlpha = Math.max(0, Math.min(1, alpha));

    // Try direct manipulation for common formats first
    if (color.startsWith("rgba(")) {
        return color.replace(/,\s*([0-9.]+)[)]/, `, ${clampedAlpha})`);
    } else if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${clampedAlpha})`);
    } else if (color.startsWith("#")) {
        if (color.length === 4) {
            // #RGB
            const r = parseInt(color[1] + color[1], 16);
            const g = parseInt(color[2] + color[2], 16);
            const b = parseInt(color[3] + color[3], 16);
            return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
        } else if (color.length === 7) {
            // #RRGGBB
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
        }
        // Invalid hex format, fall back to canvas parsing
    }

    // Fallback for named colors or other formats: use canvas to parse
    const originalFillStyle = ctx.fillStyle;
    let finalRgbaColor = `rgba(128, 128, 128, ${clampedAlpha})`; // Default fallback (e.g., gray)

    try {
        ctx.fillStyle = color;
        const parsedColorByCanvas = ctx.fillStyle;

        if (parsedColorByCanvas.startsWith("rgba(")) {
            finalRgbaColor = parsedColorByCanvas.replace(/,\s*([0-9.]+)[)]/, `, ${clampedAlpha})`);
        } else if (parsedColorByCanvas.startsWith("rgb(")) {
            const parts = parsedColorByCanvas.match(/rgb\\((\\d+),\\s*(\\d+),\\s*(\\d+)\\)/);
            if (parts) {
                finalRgbaColor = `rgba(${parts[1]}, ${parts[2]}, ${parts[3]}, ${clampedAlpha})`;
            } else {
                // If regex fails (should be rare for valid rgb)
                console.warn("Could not parse rgb from canvas:", parsedColorByCanvas);
            }
        } else {
            console.warn(
                "Canvas could not parse color to rgb/rgba:",
                color,
                "Parsed as:",
                parsedColorByCanvas
            );
        }
    } catch (e) {
        console.error("Error processing color with canvas:", color, e);
    } finally {
        ctx.fillStyle = originalFillStyle;
    }
    return finalRgbaColor;
}
